import AnimatedSection from '../../../components/base/AnimatedSection';

const leakages = [
  {
    icon: 'ri-price-tag-3-line',
    title: 'Manuel Fiyat Ayarlaması',
    description: 'Dinamik fiyatlama alt yapısı olmayan otellerde fiyatlama aksiyonları anlık alınamadığında ciro kaybına neden olur.',
    stat: '%15-20 Ciro Kaybı',
    color: 'from-gold-300 to-gold-400',
  },
  {
    icon: 'ri-time-line',
    title: 'Operasyonel Zaman Kaybı',
    description: 'Manuel rezervasyon girişi ve yönetim süreçleri aylık ciddi maliyet oluşturuyor.',
    stat: '2 Saat/Gün',
    color: 'from-gold-400 to-gold-500',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Veri Körlüğü',
    description: 'Geliri etkileyen kanal ve segmentler net şekilde izlenmediğinde veriye dayalı karar alma olanaksızlaşır.',
    stat: 'Sıfır Görünürlük',
    color: 'from-gold-300 to-gold-500',
  },
];

export default function DiagnosisSection() {
  return (
    <section id="solutions" className="relative py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-gold-100/10 to-primary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left Side - Visual/Illustration */}
          <AnimatedSection animation="fadeRight">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-gold-400/20 to-primary/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gold-200/50">
                <img
                  src="https://public.readdy.ai/ai/img_res/6a10d1454814a4c35edb2f822015254e.jpg"
                  alt="Revenue Management Dashboard"
                  className="w-full h-auto rounded-2xl object-cover"
                />
                
                {/* Floating Stats Cards */}
                <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-xl border border-gold-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white">
                      <i className="ri-arrow-down-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Kayıp Gelir</p>
                      <p className="text-lg font-bold text-red-600">₺2.4M</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-gold-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white">
                      <i className="ri-arrow-up-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Potansiyel Kazanç</p>
                      <p className="text-lg font-bold text-green-600">₺3.8M</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Right Side - Content */}
          <div>
            <AnimatedSection animation="fadeLeft">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-100 to-red-50 text-red-700 rounded-full text-sm font-semibold mb-6 border border-red-200">
                <i className="ri-error-warning-line text-lg"></i>
                Kritik Teşhis
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6 leading-tight">
                Otel İşletmelerinde{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-500 to-gold-600">
                  3 Sessiz Kâr Sızıntısı
                </span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Her gün farkında olmadan kaybettiğiniz geliri keşfedin. Bu sızıntılar, yıllık cironuzun <strong className="text-gold-600">%25-40'ına</strong> kadar zarar verebilir.
              </p>
              
              {/* Mini Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-gold-50 to-white rounded-xl p-4 border border-gold-200">
                  <p className="text-3xl font-bold text-gold-600 mb-1">₺850K+</p>
                  <p className="text-sm text-gray-600">Ortalama Yıllık Kayıp</p>
                </div>
                <div className="bg-gradient-to-br from-primary/5 to-white rounded-xl p-4 border border-primary/20">
                  <p className="text-3xl font-bold text-primary mb-1">%35</p>
                  <p className="text-sm text-gray-600">Gelir Artış Potansiyeli</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>

        {/* Leakage Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {leakages.map((item, index) => (
            <AnimatedSection key={index} animation="fadeUp" delay={index * 100}>
              <div className="group relative bg-white rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-500 border border-transparent hover:border-gold-300 h-full overflow-hidden">
                <div className="relative z-10">
                  <div className="mb-5">
                    <div
                      className={`w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-r ${item.color} text-white group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                    >
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-gold-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.description}
                  </p>

                  {/* Hover Arrow */}
                  <div className="mt-4 flex items-center gap-2 text-gold-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-sm font-semibold">Detaylı İncele</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
